/**
 * @fileoverview `VenueCoverPanel` — the cover slot of `/venue/[slug]`'s hero.
 *
 * Replaces FRE-1071's captionless gradient. That gradient was decorative and
 * silent: it told the reader nothing and read like an image that had failed to
 * load. This panel instead **carries the seed facts the detail response already
 * knows** — neighborhood, venue kind, founding year, coordinates — and states
 * the absence of a photo plainly in the corner rather than pretending.
 *
 * Two tiers, and they REPLACE each other (FRE-1323 AC5) — they never stack:
 *
 * | `data-tier` | When                     | Content                                  |
 * | ----------- | ------------------------ | ---------------------------------------- |
 * | `seed`      | no `coverUrl`            | the fact placard, `aria-hidden`          |
 * | `enriched`  | owner/admin cover exists | the real `<img>`, with a real alt        |
 *
 * The placard is `aria-hidden` on purpose (AC4). Every value on it is stated
 * again elsewhere on the page in a form assistive tech already reaches — the
 * hero eyebrow carries neighborhood and founding year, the right rail carries
 * the address, ZIP and map. Exposing the placard too would make a screen reader
 * read each fact twice for zero added information. Same call `VenueListCard`
 * makes on its `No photo yet` caption (FRE-1325): placeholder chrome must not
 * double up. The `enriched` tier is NOT hidden — a real photo is real content.
 *
 * **That premise is a standing obligation, not a one-time observation.** It is
 * what makes hiding the placard honest rather than lossy, so a value added
 * here needs a reachable home elsewhere on the page IN THE SAME CHANGE.
 * API-1712 is the worked example: `postalCode` reached the detail contract and
 * the placard could finally print `NODA · 28205`, but the hero eyebrow takes
 * no postcode and the rail's Address row is `Location.address` — the street
 * only. Shipping just the placard segment would have made the postcode visible
 * to sighted readers and to nobody else, so that change added the rail's ZIP
 * row in the same PR.
 *
 * Every line omits itself when its field is null (AC2) rather than printing a
 * placeholder, so a venue with null coordinates loses the coordinate line
 * outright instead of being mislocated the way WEB-860 describes for the map.
 *
 * @since FRE-1323
 */
import * as React from 'react';
import { cn } from '@/components/atoms/primitives/utils';
import { formatCoordPair } from '@/utils/formatCoord';
import type { VenueCoordinates } from '@/types/venueDetail';

/**
 * Stated in the corner of the `seed` placard. The venue surface has no photo
 * pipeline yet, so silence here reads as a broken image; naming the absence
 * turns it into a fact. Deliberately parallel to `VenueListCard`'s
 * `No photo yet` (FRE-1325) — same principle, different slot, so the two
 * surfaces stay one vocabulary.
 */
const NO_COVER_LABEL = 'Photo pending';

/** Mono label above the founding-year numeral. */
const EST_LABEL = 'Est.';

/**
 * Decorative fill behind the placard text — carried over unchanged from
 * FRE-1071 so the panel's colour does not shift, only its content.
 */
const PLACARD_BASE_GRADIENT = 'linear-gradient(135deg, var(--surface) 0%, var(--bg-sunken) 100%)';
const PLACARD_OVERLAY_GRADIENT =
  'radial-gradient(ellipse 70% 50% at 50% 70%, color-mix(in srgb, var(--clt-ember) 45%, transparent) 0%, transparent 50%),' +
  'radial-gradient(ellipse 50% 30% at 30% 30%, color-mix(in srgb, var(--clt-skyline) 40%, transparent) 0%, transparent 60%)';

/**
 * The placard's type and rhythm are sized in `cqi` — a share of the PANEL's
 * inline size — not in fixed pixels.
 *
 * This is load-bearing, not styling taste. The frame is a fixed 4:3 box, so its
 * height budget shrinks in proportion to its width, while fixed-px type does
 * not. Measured at 320px (a 240x180 panel) with everything but the numeral at a
 * fixed size, the content came to 207px against a 178px box: a 29px vertical
 * overflow that clipped the `Photo pending` chip out of the frame entirely, and
 * the bottom row wrapped to two lines on top of that. jsdom sees none of this —
 * it computes no layout and evaluates no container queries — which is why AC6
 * asks for real widths and why `venue-cover-panel-geometry.spec.ts` measures
 * them.
 *
 * Every ramp keeps a floor that stays legible on the smallest panel and a
 * ceiling that matches the original fixed size, so nothing above ~412px of
 * panel width changes at all.
 */
const FLUID = {
  /** `NEIGHBORHOOD · POSTCODE` eyebrow. */
  place: 'clamp(9px, 2.6cqi, 11px)',
  /** Venue kind. */
  kind: 'clamp(12px, 3.6cqi, 15px)',
  /**
   * `Est.` label and the coordinate / chip row. Held at a flat 10px rather than
   * ramped: AC3 asks for a legible `Photo pending`, and this is the smallest
   * type on the panel, so it is the one ramp not worth the vertical pixels.
   */
  fine: '10px',
  /**
   * The founding-year numeral — the panel's largest type, and the thing AC6
   * names as most likely to overflow.
   */
  year: 'clamp(30px, 18cqi, 76px)',
  /**
   * Padding and the gap between the three stacked blocks.
   *
   * These sit on the inner `<div>`, NOT on the panel. A container-query unit
   * resolves against the nearest ANCESTOR container, so `cqi` written on the
   * container element itself silently falls back to the small viewport — which
   * at 320px made the padding 12.8px instead of 10px and overflowed the frame
   * by 1.4px, pushing the `Photo pending` chip past the clip edge. Measured, not
   * theorised; the geometry spec catches it if it comes back.
   */
  pad: 'clamp(10px, 4cqi, 20px)',
  gap: 'clamp(6px, 3.9cqi, 16px)',
} as const;

/** Trimmed value, or `null` for nullish / whitespace-only input. */
function cleanText(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Whole-number year, or `null`.
 *
 * `Number.isInteger` rather than `Number.isFinite`: it rejects `NaN` and
 * `Infinity` on the same test, and it also rejects a fractional year, which
 * `isFinite` would let through to render as `Est. 2001.5`. The wire contract
 * (`foundedYear: z.number().int()`) already guarantees an integer, so this is
 * belt-and-braces — but the prop is plain `number`, so a caller that is not the
 * detail response can pass anything, and the numeral is the panel's largest
 * type: a wrong value there is the most visible one on the placard.
 */
function cleanYear(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isInteger(value) ? value : null;
}

export interface VenueCoverPanelProps {
  /**
   * Neighborhood display name (e.g. `NoDa`), uppercased for the top line.
   * Derived from `VenueDetailResponse.neighborhoodSlug` by the caller.
   */
  neighborhoodName?: string | null;
  /**
   * Postal code, appended after the neighborhood as `NODA · 28205`.
   *
   * On the venue detail contract since API-1712, and wired through from
   * `VenueDetailTemplate`. The route already trims a blank column to `null`,
   * so `cleanText` here is belt-and-braces for callers that are not the detail
   * response.
   */
  postalCode?: string | null;
  /**
   * Venue kind (e.g. `Concert hall`).
   *
   * Humanised `Location.venueType` from the venue-detail wire (API-1721).
   * Print as-is. Null / blank omits the line (AC2) — never invent "Other".
   */
  kind?: string | null;
  /** Founding year; drives the `Est. YYYY` display numeral. */
  foundedYear?: number | null;
  /** Venue coordinates; drives the degree line. Null omits the line entirely. */
  coordinates?: VenueCoordinates | null;
  /**
   * Owner/admin-uploaded cover. When present the placard is REPLACED by the
   * photo panel (`data-tier="enriched"`) rather than layered under it.
   */
  coverUrl?: string | null;
  /** Alt text for the real cover. Required in practice when `coverUrl` is set. */
  coverAlt?: string | null;
  className?: string;
}

/** Shared frame geometry — identical across both tiers so the slot never shifts. */
const PANEL_FRAME =
  'relative aspect-[4/3] w-full overflow-hidden rounded-ds-lg border border-ds-border';

export function VenueCoverPanel({
  neighborhoodName,
  postalCode,
  kind,
  foundedYear,
  coordinates,
  coverUrl,
  coverAlt,
  className,
}: VenueCoverPanelProps): React.ReactElement {
  const cover = cleanText(coverUrl);

  if (cover) {
    return (
      <div
        data-testid="venue-cover-panel"
        data-tier="enriched"
        className={cn(PANEL_FRAME, className)}
      >
        {/* Plain <img>, not next/image: cover URLs are operator-supplied and
            not on a configured loader domain. No CLS risk — the 4:3 frame
            reserves the box before the image resolves. */}
        <img
          data-testid="venue-cover-photo"
          src={cover}
          alt={cleanText(coverAlt) ?? ''}
          className="absolute inset-0 h-full w-full object-cover"
        />
      </div>
    );
  }

  const neighborhood = cleanText(neighborhoodName);
  const place = [neighborhood === null ? null : neighborhood.toUpperCase(), cleanText(postalCode)]
    .filter((segment): segment is string => segment !== null)
    .join(' · ');
  const kindLabel = cleanText(kind);
  const year = cleanYear(foundedYear);
  const coordLabel = coordinates ? formatCoordPair(coordinates.lat, coordinates.lng) : null;

  return (
    <div
      // AC4 — decorative reinforcement of facts the page already states in the
      // a11y tree. Hidden as a whole, including the `Photo pending` chip.
      aria-hidden="true"
      data-testid="venue-cover-panel"
      data-tier="seed"
      // `@container` makes the panel the query container everything below
      // sizes against (the `cqi` ramps in `FLUID`). Without it they would fall
      // back to the viewport and stop tracking the slot.
      className={cn(PANEL_FRAME, '@container', className)}
      style={{ background: PLACARD_BASE_GRADIENT }}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ background: PLACARD_OVERLAY_GRADIENT }}
      />

      <div
        data-testid="venue-cover-frame"
        className="relative flex h-full flex-col justify-between"
        style={{ padding: FLUID.pad, gap: FLUID.gap }}
      >
        <div className="flex min-w-0 flex-col gap-1">
          {place && (
            <p
              data-testid="venue-cover-place"
              className="m-0 truncate font-mono uppercase tracking-[0.16em] text-accent-text"
              style={{
                // Ink is `text-accent-text` (`--accent-text`), NOT the bare
                // `--clt-skyline-glow`. The raw
                // glow (#4fc4c4) is theme-invariant, so as ink on this panel's
                // light-theme ground it measures 2.09:1 in Chromium — a WCAG
                // 1.4.3 AA failure. `--accent-text` resolves to the AA-safe
                // pair (#0a6363 light, byte-identical #4fc4c4 dark). The
                // repo's `link-accent-ink` guard names this exact hazard but
                // is scoped to anchors, so nothing else was catching it here.
                fontSize: FLUID.place,
              }}
            >
              {place}
            </p>
          )}
          {kindLabel && (
            <p
              data-testid="venue-cover-kind"
              className="m-0 truncate font-body text-loci-text"
              style={{ fontSize: FLUID.kind }}
            >
              {kindLabel}
            </p>
          )}
        </div>

        {year !== null && (
          <p data-testid="venue-cover-founded" className="m-0 flex min-w-0 flex-col text-loci-text">
            <span
              data-testid="venue-cover-est-label"
              className="font-mono uppercase tracking-[0.16em] text-loci-text-muted"
              style={{ fontSize: FLUID.fine }}
            >
              {EST_LABEL}
            </span>
            <span
              data-testid="venue-cover-founded-year"
              className="font-display font-bold leading-[0.9] tracking-[-0.02em] tabular-nums"
              style={{
                // AC6 — `cqi` scales the numeral against the PANEL, not the
                // viewport, so it stays inside the slot at every width regardless
                // of how the hero grid reflows around it.
                fontSize: FLUID.year,
              }}
            >
              {year}
            </span>
          </p>
        )}

        {/*
          `justify-end` + `mr-auto`, NOT `justify-between`.

          The coordinate line carries `truncate` (`white-space: nowrap`), so
          its flex base size is its max-content width and it cannot shrink to
          avoid a break. At 320px the two items need ~277px against a ~218px
          row, so the row DOES wrap — and `justify-between` on the resulting
          one-item line resolves to flex-start, which would put the chip hard
          LEFT, the opposite of the design. An auto margin does the same job
          on one line and degrades correctly onto two.
        */}
        <div className="flex min-w-0 flex-wrap items-end justify-end gap-x-3 gap-y-2">
          {coordLabel && (
            <span
              data-testid="venue-cover-coords"
              className="mr-auto min-w-0 truncate font-mono tracking-[0.08em] text-loci-text-muted"
              style={{ fontSize: FLUID.fine }}
            >
              {coordLabel}
            </span>
          )}
          <span
            data-testid="venue-cover-no-cover"
            className={cn(
              'inline-flex shrink-0 items-center whitespace-nowrap',
              // Chip construction mirrors `VenueListCard`'s `No photo yet` chip
              // so the two absence states read as one system (FRE-1325).
              'rounded-full border px-2 py-0.5',
              'uppercase tracking-[0.16em] font-mono',
              'border-ds-border bg-bg-sunken text-loci-text',
            )}
            style={{
              // Palette is token-backed rather than copied wholesale from
              // `VenueListCard`, which paints white on `bg-black/40`. That chip
              // always sits on the card's navy -> midnight hero, so it is on a
              // dark ground in BOTH themes. This placard's ground is
              // `--surface` -> `--bg-sunken`, which INVERTS with the theme:
              // measured in Chromium, white on the composited 40%-black over
              // the light surface came out around 3:1, under WCAG 1.4.3 AA for
              // text this size. `aria-hidden` does not exempt it — 1.4.3 is
              // about text presented visually.
              fontSize: FLUID.fine,
            }}
          >
            {NO_COVER_LABEL}
          </span>
        </div>
      </div>
    </div>
  );
}

export { NO_COVER_LABEL };
